import React, { useState } from 'react';
import PropTypes from 'prop-types';
import IronSightMark from '../../shared/IronSightMark';
import { Skeleton } from '../../ui';
import { formatCurrency, formatNumber, validPrice } from '../../../utils/formatting';
import { aiPriceGapDisplay, consistentAiRange } from '../../../utils/aiPrice';
import { grossMarginGood } from '../../../utils/unitIntelligence';
import { useDealershipSettings } from '../../../contexts/settingsContext';
import useMarginFloorPct from '../../../hooks/useMarginFloor';
import {
    changesNewestFirst, fmtHistDate, fmtPricePct, priceChangeDollars, priceChangeTone, priceHistoryPoints,
    twelveMonthValueChange, DAY_MS,
} from '../../../utils/unitPricing';
import { UnitChart, niceTicks } from '../charts';
import { tabCtxShape } from './tabContext';
import NotForCommitted from '../NotForCommitted';
import './PricingTabV2.css';

// Increase green, reduction orange (amber token), flat muted — today's rule.
const TONE_CLASS = { up: 'u2-pos', down: 'u2-warn', flat: 'u2-quiet' };
const LINE_TONE = { up: 'sage', down: 'amber', flat: 'grey' };
// Gap state → token text class (same scale as the table chip).
const GAP_CLASS = { inline: 'u2-pos', above: 'u2-warn', wellAbove: 'u2-neg', below: 'u2-info', review: 'u2-quiet' };

const kMoney = (v) => (Math.abs(v) >= 1000 ? `$${Number((v / 1000).toFixed(1))}k` : formatCurrency(v));
const fmtMoneySigned = (v) => `${v < 0 ? '−' : v > 0 ? '+' : ''}${formatCurrency(Math.abs(v))}`;
const monthLabel = (t) => new Date(t).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

/** The price line as a step chart with the AI today line (N28). */
function PriceHistoryChart({ unit, history }) {
    const now = Date.now();
    const pts = priceHistoryPoints(unit, history, now);
    const t0 = pts[0].t;
    const days = (t) => (t - t0) / DAY_MS;
    const xMax = Math.max(1, days(Math.max(now, pts[pts.length - 1].t)));
    const ai = validPrice(unit.PredictedPrice);
    const prices = pts.map((p) => p.price).concat(ai ? [ai] : []);
    const { lo, hi, ticks } = niceTicks(Math.min(...prices), Math.max(...prices), 3);
    const tone = LINE_TONE[priceChangeTone(history.total_change_pct)];
    const series = pts.map((p) => [days(p.t), p.price]);
    const label = `Asking price over time: ${formatCurrency(pts[0].price)} at listing to ${formatCurrency(history.current_price)} today.`;
    return (
        <UnitChart
            label={label}
            height={170}
            x={[0, xMax]}
            y={[lo, hi]}
            series={series}
            step
            lineTone={tone}
            yticks={ticks.map((v) => [v, kMoney(v)])}
            xticks={[[0, monthLabel(t0)], [xMax / 2, monthLabel(t0 + (xMax / 2) * DAY_MS)], [xMax, 'Today']]}
            refs={ai ? [{ y: ai, label: `AI today ${kMoney(ai)}`, tone: 'sage', dashed: false }] : []}
            dots={pts.map((p, i) => ({
                x: series[i][0],
                y: p.price,
                r: p.kind === 'current' ? 5.5 : 4,
                hollow: p.kind === 'origin',
                tone,
                tip: `${p.kind === 'current' ? 'Today' : fmtHistDate(new Date(p.t).toISOString())} · ${formatCurrency(p.price)}`,
            }))}
            readout={(i) => `${pts[i].kind === 'current' ? 'Today' : fmtHistDate(new Date(pts[i].t).toISOString())}: ${formatCurrency(pts[i].price)}`}
            table={pts.map((p, i) => [`${i + 1}. ${p.kind === 'current' ? 'Today' : fmtHistDate(new Date(p.t).toISOString())}`, formatCurrency(p.price)])}
        />
    );
}
PriceHistoryChart.propTypes = { unit: PropTypes.object.isRequired, history: PropTypes.object.isRequired };

/** Price history (P-UNIT-082): today's states, summary, chart and table. */
function PriceHistoryCard({ unit, history, loading, error }) {
    // A unit not yet received (pre-sold) has no days on the market to tell.
    const noDays = unit.DaysInStock == null;
    const [expanded, setExpanded] = useState(false);
    let body;
    let sub = null;
    if (loading) {
        body = <div aria-label="Loading price history"><Skeleton lines={3} /></div>;
    } else if (error) {
        body = <p className="u2-note u2pr-err" role="alert">{error}</p>;
    } else if (!history) {
        body = <p className="u2-note u2pr-err">Price history unavailable.</p>;
    } else if (!history.num_changes && !(history.current_price > 0)) {
        // No asking price (QA E8): never "asking $0".
        body = <p className="u2-note">No price changes — No asking price.</p>;
    } else if (!history.num_changes && noDays) {
        body = <p className="u2-note">No price changes — asking <b>{formatCurrency(history.current_price)}</b>.</p>;
    } else if (!history.num_changes) {
        body = (
            <p className="u2-note">
                No price changes — listed at <b>{formatCurrency(history.current_price)}</b> since{' '}
                {formatNumber(history.days_on_market)} days ago.
            </p>
        );
    } else {
        const tone = TONE_CLASS[priceChangeTone(history.total_change_pct)];
        const dollars = priceChangeDollars(history);
        sub = (
            <span className="u2-sub u2pr-sum">
                {history.num_changes} price change{history.num_changes !== 1 ? 's' : ''} ·{' '}
                <b className={tone}>{fmtPricePct(history.total_change_pct)}</b>
                {dollars != null && <b className={tone}> ({fmtMoneySigned(dollars)})</b>}
                {!noDays && <>{' '}over {formatNumber(history.days_on_market)} days</>}
            </span>
        );
        body = (
            <>
                <PriceHistoryChart unit={unit} history={history} />
                <button type="button" className="u2-linkb" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
                    {expanded ? 'Hide changes' : 'View all changes'}
                </button>
                {expanded && (
                    <div className="u2-tw">
                        <table className="u2-tt">
                            <thead><tr><th>Date</th><th>Old → New</th><th className="r">Change</th></tr></thead>
                            <tbody>
                                {changesNewestFirst(history.changes).map((c, i) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <tr key={`${c.date}-${i}`}>
                                        <td>{fmtHistDate(c.date)}</td>
                                        <td>{formatCurrency(c.old_price)} → {formatCurrency(c.new_price)}</td>
                                        <td className={`r u2pr-chg ${TONE_CLASS[priceChangeTone(c.change_pct)]}`}>{fmtPricePct(c.change_pct)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        );
    }
    return (
        <section className="u2-card" aria-labelledby="u2pr-ph">
            <div className="u2-c-head">
                <span className="u2-is-mark"><IronSightMark /> IronSight</span>
                <h3 id="u2pr-ph">Price history</h3>
                {sub}
            </div>
            {body}
        </section>
    );
}
PriceHistoryCard.propTypes = {
    unit: PropTypes.object.isRequired, history: PropTypes.object, loading: PropTypes.bool, error: PropTypes.string,
};

function Row({ label, children, className }) {
    return <div><dt>{label}</dt><dd className={className}>{children}</dd></div>;
}
Row.propTypes = { label: PropTypes.node, children: PropTypes.node, className: PropTypes.string };

/**
 * Pricing & Usage (P-UNIT-080…083) in the prototype's two columns: price
 * history and pricing on the left; AI price comparison, usage & meters and
 * the cost of waiting (N27) on the right. No "Prediction history" (D8).
 */
export default function PricingTabV2({ ctx }) {
    const {
        unit, detail, goTab, committed,
    } = ctx;
    const { gapThresholds } = useDealershipSettings();
    const marginFloorPct = useMarginFloorPct();
    const pending = unit.StockStatus === 'P';
    const aiPrice = unit.PredictedPrice || null;
    const asking = unit.Best_Sell_Price;
    const gap = aiPrice && asking ? aiPriceGapDisplay(unit, gapThresholds) : null;
    const range = consistentAiRange(unit);
    const valueChange = twelveMonthValueChange(unit, detail.predictions12m);
    const max = Math.max(asking || 0, aiPrice || 0) || 1;

    return (
        <div className="u2-grid2">
            <div className="u2-col">
                <PriceHistoryCard unit={unit} history={detail.priceHistory} loading={detail.priceHistoryLoading} error={detail.priceHistoryError} />
                <section className="u2-card" aria-labelledby="u2pr-pr">
                    <div className="u2-c-head"><h3 id="u2pr-pr">Pricing</h3></div>
                    <dl className="u2-dl">
                        <Row label="Asking price">{formatCurrency(unit.Best_Sell_Price)}</Row>
                        <Row label="Book cost">{formatCurrency(unit.Sales_Book_Value)}</Row>
                        {unit.OVRALLOW != null && unit.OVRALLOW !== 0 && <Row label="Over allowance">{formatCurrency(unit.OVRALLOW)}</Row>}
                        <Row label="Net cost">{formatCurrency(unit.InternalPrice)}</Row>
                        {unit.GrossMargin != null && (
                            <Row label="Gross margin" className={grossMarginGood(unit.GrossMarginPct, marginFloorPct) ? 'u2-pos' : 'u2-warn'}>
                                {formatCurrency(unit.GrossMargin)} ({unit.GrossMarginPct?.toFixed(1)}%)
                            </Row>
                        )}
                        <Row label={pending ? 'Cost estimate' : 'True cost'}>{formatCurrency(unit.CarryingCostBasis)}</Row>
                    </dl>
                </section>
            </div>

            <div className="u2-col">
                {gap && (
                    <section className="u2-card" aria-labelledby="u2pr-ai">
                        <div className="u2-c-head">
                            <span className="u2-is-mark"><IronSightMark /> IronSight</span>
                            <h3 id="u2pr-ai">AI price comparison</h3>
                        </div>
                        <div className="u2pr-bars">
                            <span className="u2pr-bl">Asking</span>
                            <span className="u2pr-track"><i className="is-ask" style={{ width: `${Math.min(100, (asking / max) * 100)}%` }} /></span>
                            <span className="u2pr-bv">{formatCurrency(asking)}</span>
                            <span className="u2pr-bl">AI est.</span>
                            <span className="u2pr-track"><i className="is-ai" style={{ width: `${Math.min(100, (aiPrice / max) * 100)}%` }} /></span>
                            <span className="u2pr-bv u2-pos">{formatCurrency(aiPrice)}</span>
                        </div>
                        <p className={`u2-note u2pr-gap ${GAP_CLASS[gap.state] || ''}`}>
                            Gap: {gap.pctText} — {gap.label}
                        </p>
                        {range && <p className="u2-note">Likely range {formatCurrency(range.low)}–{formatCurrency(range.high)}</p>}
                    </section>
                )}

                <section className="u2-card" aria-labelledby="u2pr-us">
                    <div className="u2-c-head"><h3 id="u2pr-us">Usage &amp; meters</h3></div>
                    <dl className="u2-dl">
                        <Row label="Hours / odometer">{formatNumber(unit.ODOMETER)}</Row>
                        <Row label="Usage type">{unit.Usage || 'N/A'}</Row>
                        <Row label="Separator meter">{unit.Sep_Meter ? formatNumber(unit.Sep_Meter) : 'N/A'}</Row>
                    </dl>
                </section>

                {committed && <NotForCommitted title="Cost of waiting" />}
                {!committed && (unit.CarryingCostBasis != null || unit.CarryingCostDaily != null || valueChange != null) && (
                    <section className="u2-card" aria-labelledby="u2pr-cw">
                        <div className="u2-c-head"><h3 id="u2pr-cw">Cost of waiting</h3><span className="u2-sub">full detail on Intelligence</span></div>
                        <dl className="u2-kv">
                            <dt>Carrying cost to date</dt>
                            <dd>{pending ? '$0' : formatCurrency(unit.CarryingCostToDate)}</dd>
                            <dt>Daily carrying cost</dt>
                            <dd>{pending ? '$0 · not yet on books' : formatCurrency(unit.CarryingCostDaily)}</dd>
                            {valueChange != null && (
                                <>
                                    <dt>12-month value change</dt>
                                    <dd className={valueChange < 0 ? 'u2-neg' : 'u2-pos'}>{fmtMoneySigned(valueChange)}</dd>
                                </>
                            )}
                        </dl>
                        <button type="button" className="u2-linkb" onClick={() => goTab('ai', { anchor: 'u2-carry' })}>
                            Open carrying cost and margin calculator →
                        </button>
                    </section>
                )}
            </div>
        </div>
    );
}

PricingTabV2.propTypes = { ctx: tabCtxShape.isRequired };
